/* import __COLOCATED_TEMPLATE__ from './posting-create-application.hbs'; */
import { computed } from '@ember/object';
import { readOnly, equal } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import {
  POSTING_CREATE_TYPE_NAMES,
  POSTING_CREATE_DATE_RANGE_TYPE,
  POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP
} from 'sh-www/constants/routes/posting-create';
import { ASSIGNMENT } from 'sh-www/models/assignment';

const CLASS_NAME = 'SH-c-posting-create-application';

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: '',
  CLASS_NAME,

  /**
   * Register services
   */
  moment: service(),
  state: service('states/routes/posting-create'),
  stateManager: service('state-managers/routes/posting-create'),

  /**
   * @type {Boolean}    True when the form elements should be disabled
   */
  _areFormElementsDisabled: readOnly('state.areFormElementsDisabled'),

  /**
   * @type {String}     Constants for the date range enums in the template file.
   */
  _constantDateRangeSelectADate: POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE,
  _constantWithin24Hours: POSTING_CREATE_DATE_RANGE_TYPE.WITHIN_24_HOURS,

  /**
   * @type {String}     Constants for the posting create type name enums in the template file.
   */
  _constantPostingCreateTypeNameStandard: POSTING_CREATE_TYPE_NAMES[ASSIGNMENT.TYPES.STANDARD],

  /**
   * @type {String}     Title for public privacy level
   */
  _constantPrivacyLevelPublicTitle: POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP[ASSIGNMENT.PRIVACY_LEVEL.PUBLIC],
  _constantPrivacyLevelPublicEnum: ASSIGNMENT.PRIVACY_LEVEL.PUBLIC,
  _privacyLevelPublicSelected: equal('_updatedPrivacyLevel', ASSIGNMENT.PRIVACY_LEVEL.PUBLIC),

  /**
   * @type {String}     Title for network privacy level
   */
  _constantPrivacyLevelNetworkTitle: POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP[ASSIGNMENT.PRIVACY_LEVEL.NETWORK],
  _constantPrivacyLevelNetworkEnum: ASSIGNMENT.PRIVACY_LEVEL.NETWORK,
  _privacyLevelNetworkSelected: equal('_updatedPrivacyLevel', ASSIGNMENT.PRIVACY_LEVEL.NETWORK),

  /**
   * @type {String}     Title for private privacy level
   */
  _constantPrivacyLevelPrivateTitle: POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP[ASSIGNMENT.PRIVACY_LEVEL.PRIVATE],
  _constantPrivacyLevelPrivateEnum: ASSIGNMENT.PRIVACY_LEVEL.PRIVATE,
  _privacyLevelPrivateSelected: equal('_updatedPrivacyLevel', ASSIGNMENT.PRIVACY_LEVEL.PRIVATE),

  /**
   * @type {Date|null}    The current selected for the calendar input.
   */
  _dateStartingValue: computed('state.updatedDateSelected', function() {
    const currentSelectedDate = this.get('state.updatedDateSelected');

    if (!currentSelectedDate) {
      return null;
    }

    return currentSelectedDate.toDate();
  }),

  /**
   * @type {Boolean}    True when we have a deadline date error.
   */
  _isHasDeadlineDateError: readOnly('state.isDeadlineDateExpired'),

  /**
   * @type {Boolean}    Booleans for the block buttons for what type of date range is selected.
   */
  _isSelectADateSelected: equal('_updatedDateRangeType', POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE),
  _isWithin24HoursSelected: equal('_updatedDateRangeType', POSTING_CREATE_DATE_RANGE_TYPE.WITHIN_24_HOURS),

  /**
   * @type {Boolean}    When this is true we will trigger the date selector in our form.
   */
  _isShowDateSelector: false,

  /**
   * @type {String}     Description of selected privacy level
   */
  _postingPrivacyLevelDescription: '',

  /**
   * @type {String}     Link to selected privacy level explaination
   */
  _postingPrivacyLevelDescriptionLink: '',

  /**
   * @type {Boolean}    True if this is an urgent assignment.
   */
  _updatedIsUrgent: readOnly('state.updatedIsUrgent'),

  /**
   * This is for the 'find-freelancer' mode
   *
   * @type {Date}       The minimum date selectable for the select a date, date picker
   */
  _minDateAllowed: computed('state.updatedPostingTypeName', function() {
    if (this.state.updatedPostingTypeName === POSTING_CREATE_TYPE_NAMES[ASSIGNMENT.TYPES.PITCH]) {
      return this.moment.moment().add(7, 'day').toDate();
    }

    return this.moment.moment().add(1, 'day').toDate();
  }),

  /**
   * This is for the 'pitch' mode
   *
   * @type {Date}       The minimum date selectable for the select a date, date picker
   */
  _minDateForPostingCustomDate: computed(function() {
    return this.get('moment').moment().add(7, 'days').toDate();
  }),

  /**
   * @type {String}   This is the title of the date selection block button.  Will change based on if date is selected.
   *
   */
  _selectADateTitle: computed(
  'state.updatedDateSelected',
  'state.updatedDateRangeType',
  function() {
    if (this.get('state.updatedDateSelected') && this.get('state.updatedDateRangeType') === POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE) {
      return this.get('state.updatedDateSelected').format('MM/DD/YYYY');
    }

    return 'Select a date';
  }),

  /**
   * @type {String}     of type POSTING_CREATE_DATE_RANGE_TYPE.*
   */
  _updatedDateRangeType: readOnly('state.updatedDateRangeType'),

  /**
   * This is the privacy level the user has selected for the posting.
   *
   * @type {Number}     of type Assignment.PRIVACY_LEVEL_*
   */
  _updatedPrivacyLevel: readOnly('state.updatedPrivacyLevel'),

  actions: {
    /**
     * This is called when the user selects a date from our date selector
     *
     * @param {Date} date
     */
    onDateSelected: function(date) {
      this.stateManager.updateDateRangeType(POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE);

      this.get('stateManager').updateDeadlineDate(this.get('moment').moment(date));

      this.setProperties({
        'state.errors': {
          ...this.state.errors,
          deadline: undefined,
        },
        _isShowDateSelector: false,
      });
    },

    /**
     * This is closed when the date selector input has closed.
     */
    onDateSelectorClosed: function() {
      this.set('_isShowDateSelector', false);
    },

    /**
     * When mouse leaves, close the date selector if it's open.
     */
    onMouseLeaveCalendar: function() {
      this.set('_isShowDateSelector', false);
    },

    /**
     * Called when the user selects a new privacy level
     *
     * @param {String} privacyLevel     of type Assignment.PRIVACY_LEVEL* but needs to be parsed to int
     */
    onPrivacyLevelSelected: function(privacyLevel) {
      this.stateManager.updatePrivacyLevel(privacyLevel);

      this.setProperties({
        'state.errors': {
          ...this.state.errors,
          privacy_level: undefined,
        },
        _postingPrivacyLevelDescription: ASSIGNMENT.PRIVACY_LEVEL_DESCRIPTIONS[privacyLevel]?.text ?? '',
        _postingPrivacyLevelDescriptionLink: ASSIGNMENT.PRIVACY_LEVEL_DESCRIPTIONS[privacyLevel]?.link ?? '',
      });
    },

    /**
     * Called when the user selects a date range enum.
     *
     * @param {String} dateRangeTypeSelected    of type POSTING_CREATE_DATE_RANGE_TYPE.*
     */
    onSelectDateTypeButtonClicked: function(dateRangeTypeSelected) {
      this.setProperties({
        'state.errors': {
          ...this.state.errors,
          deadline: undefined,
        },
        _isShowDateSelector: dateRangeTypeSelected === POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE,
      });

      this.get('stateManager').updateDateRangeType(dateRangeTypeSelected);
    }
  }
});
